/**
 * A chosen picture, fitted to what the notetaker's camera tile takes
 * (2026-09-30): Recall's rules — a JPEG, 1280×720, under 1.3 MB.
 *
 * Fitted here rather than refused: a company's picture is a logo on a slide,
 * a banner, a photo, never exactly 1280×720. The whole of it is kept — scaled
 * to fit, never cropped, on the tile's own dark ground — because some meeting
 * apps crop the edges of a tile already, and a logo cut twice is no logo.
 */
export const TILE_W = 1280;
export const TILE_H = 720;
/** Recall's ceiling, in bytes of JPEG. */
const MOST_BYTES = 1_300_000;
/** The default tiles' ground, so a picture narrower than 16:9 sits on the same dark. */
const GROUND = '#1f2023';

export async function toTile(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = TILE_W;
  canvas.height = TILE_H;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot draw the picture.');
  context.fillStyle = GROUND;
  context.fillRect(0, 0, TILE_W, TILE_H);
  const scale = Math.min(TILE_W / bitmap.width, TILE_H / bitmap.height);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  context.drawImage(bitmap, Math.round((TILE_W - w) / 2), Math.round((TILE_H - h) / 2), w, h);
  bitmap.close();
  for (const quality of [0.92, 0.85, 0.75, 0.6]) {
    const url = canvas.toDataURL('image/jpeg', quality);
    if (bytesOf(url) <= MOST_BYTES) return url;
  }
  throw new Error('That picture is too detailed to fit in 1.3 MB — try a simpler one.');
}

/** The size of the JPEG a base64 data URL carries. */
function bytesOf(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return (
    Math.floor((base64.length * 3) / 4) - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0)
  );
}
